import { Helmet } from "react-helmet-async";

const SITE_NAME = "MyStreetzMag";
const DEFAULT_DESC =
    "MyStreetzMag — the sound of the streets. Afrobeats, Naija street culture, music, interviews and more.";
const DEFAULT_IMAGE =
    "https://customer-assets.emergentagent.com/job_streetz-media-ai/artifacts/by2uhrda_CROPPED%20white%20mystreetz.jpeg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function abs(url) {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (typeof window === "undefined") return url;
    if (url.startsWith("/")) return `${window.location.origin}${url}`;
    return url;
}

export default function SEO({
    title,
    description,
    image,
    type = "website",
    canonical,
    keywords = [],
    jsonLd,
    noindex = false,
    ogSlug,
}) {
    const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — The Sound of the Streets`;
    const desc = (description || DEFAULT_DESC).slice(0, 300);
    const dynamicOg = ogSlug && BACKEND_URL ? `${BACKEND_URL}/api/og/${ogSlug}.png` : "";
    const img = abs(dynamicOg || image || DEFAULT_IMAGE);
    const url =
        typeof window !== "undefined"
            ? canonical || `${window.location.origin}${window.location.pathname}`
            : canonical || "";

    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
            {url && <link rel="canonical" href={url} />}
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph */}
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:image" content={img} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={fullTitle} />
            {url && <meta property="og:url" content={url} />}

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={img} />
            <meta name="twitter:image:alt" content={fullTitle} />

            {/* JSON-LD structured data */}
            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
}

export function organizationJsonLd() {
    return {
        "@context": "https://schema.org",
        "@type": "NewsMediaOrganization",
        name: SITE_NAME,
        url: typeof window !== "undefined" ? window.location.origin : "",
        logo: DEFAULT_IMAGE,
        sameAs: [
            "https://instagram.com/mystreetzmag",
            "https://twitter.com/mystreetzmag",
            "https://tiktok.com/@mystreetzmag",
            "https://youtube.com/@mystreetzmag",
        ],
    };
}

export function articleJsonLd(article) {
    return {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: article.title,
        description: article.excerpt,
        image: article.image_url ? [abs(article.image_url)] : [DEFAULT_IMAGE],
        datePublished: article.published_at || article.created_at,
        dateModified: article.published_at || article.created_at,
        articleSection: article.category,
        keywords: (article.tags || []).join(", "),
        author: { "@type": "Organization", name: SITE_NAME },
        publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
        },
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": typeof window !== "undefined" ? `${window.location.origin}/article/${article.slug}` : "",
        },
    };
}
